import { FLAG_SIZES } from '@/src/constants/ui/flagSizes'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PublicIcon from '@mui/icons-material/Public'
import {
    Box,
    Card,
    CardContent,
    Divider,
    ListItemIcon,
    MenuItem,
    Popover,
    Select,
    SelectChangeEvent,
    Typography
} from '@mui/material'
import { green } from '@mui/material/colors'
import React from 'react'

// Export types first to avoid conflicts
export interface CountryOption {
    code: string
    label: string
    domain: string // Production domain
    qaDomain: string // QA domain
    Flag?: React.ComponentType<{ width?: number; height?: number; style?: React.CSSProperties }>
}

interface LanguageOption {
    code: string
    label: string
    Flag: React.ComponentType<any>
    color?: string // Optional color for border/highlight
}

export interface LanguageCurrencyDropdownProps {
    selectedLanguage: LanguageOption
    selectedCurrency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }
    selectedCountry: CountryOption
    countryOptions: CountryOption[]
    languageOptions: LanguageOption[]
    currencyOptions: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }[]
    onLanguageSelect: (lang: LanguageOption) => void
    onCurrencySelect: (curr: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }) => void
    onCountrySelect: (country: CountryOption) => void
    open: boolean
    anchorEl: HTMLElement | null
    onOpen: (event: React.MouseEvent<HTMLElement>) => void
    onClose: () => void
    t: (key: string) => string
    tCurrency: (key: string) => string
    countrySelectOpen: boolean
    onCountrySelectOpen: () => void
    onCountrySelectClose: () => void
    onCountryChange: (event: SelectChangeEvent) => void
    isOffersPage?: boolean
}

const LanguageCurrencyDropdown: React.FC<LanguageCurrencyDropdownProps> = ({
    selectedLanguage,
    selectedCurrency,
    selectedCountry,
    countryOptions,
    languageOptions,
    currencyOptions,
    onLanguageSelect,
    onCurrencySelect,
    onCountrySelect,
    onCountryChange,
    open,
    anchorEl,
    onOpen,
    onClose,
    t,
    tCurrency,
    countrySelectOpen,
    onCountrySelectOpen,
    onCountrySelectClose,
    isOffersPage = false
}) => {
    const [currencySelectOpen, setCurrencySelectOpen] = React.useState(false)
    const handleCurrencySelectOpen = () => setCurrencySelectOpen(true)
    const handleCurrencySelectClose = () => setCurrencySelectOpen(false)
    const handleCurrencyChange = (event: SelectChangeEvent) => {
        const code = event.target.value
        const found = currencyOptions.find(c => c.code === code)
        if (found) onCurrencySelect(found)
        handleCurrencySelectClose()
    }
    return (
        <>
            {/* Header Button - compact */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1.2,
                    px: 1.5,
                    py: 0.7,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    cursor: 'pointer',
                    gap: 0.7,
                    minWidth: 80,
                    height: 34,
                    m: 1
                }}
                onClick={onOpen}
            >
                <Typography sx={{ fontWeight: 500, fontSize: 13 }}>{selectedLanguage.label}</Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.7, borderColor: 'grey.300' }} />
                <Typography sx={{ fontWeight: 500, fontSize: 13 }}>{selectedCurrency.code}</Typography>
            </Box>
            {/* Popover - compact */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: 2,
                        minWidth: 210,
                        boxShadow: 3,
                        p: 0,
                        overflow: 'hidden',
                        my: 1.5
                    }
                }}
            >
                <Box sx={{ p: 2, minWidth: 180, maxWidth: 260 }}>
                    {/* Change Language */}
                    <Typography variant="subtitle2" sx={{ color: '#232323', fontSize: 15, fontWeight: 600, mb: 1.5 }}>
                        {t('changeLanguage')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        {languageOptions.map(lang => {
                            const isSelected = selectedLanguage.code === lang.code
                            return (
                                <Card
                                    key={lang.code}
                                    sx={{
                                        width: 140,
                                        cursor: 'pointer',
                                        border: (t) => isSelected ? `1px solid ${t.palette.primary.main}` : '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s ease-in-out',
                                        boxShadow: isSelected ? 2 : 0
                                    }}
                                    onClick={() => onLanguageSelect(lang)}
                                >
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: '10px !important',
                                            pb: '8px !important',
                                            pt: '8px !important'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: 13,
                                                    color: '#111827'
                                                }}
                                            >
                                                {lang.label}
                                            </Typography>
                                        </Box>
                                        {isSelected && (
                                            <CheckCircleIcon
                                                color='primary'
                                                sx={{
                                                    // color: green[500],
                                                    fontSize: 18,
                                                    ml: 1
                                                }}
                                            />
                                        )}
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </Box>
                    {/* Change Country - Hidden on offers page */}
                    {!isOffersPage && (
                        <>
                            <Typography
                                variant="subtitle2"
                                sx={{ color: '#232323', fontSize: 15, fontWeight: 600, mb: 1.5, mt: 2 }}
                            >
                                {t('changeCountry')}
                            </Typography>
                            <Select
                                value={selectedCountry.code}
                                onChange={onCountryChange}
                                open={countrySelectOpen}
                                onOpen={onCountrySelectOpen}
                                onClose={onCountrySelectClose}
                                variant="outlined"
                                size="small"
                                sx={{
                                    width: '100%',
                                    borderRadius: 1.2,
                                    fontSize: 13,
                                    minHeight: 30,
                                    background: '#fff',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        border: '1px solid',
                                        borderColor: '#E5E7EB'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        border: '1px solid',
                                        borderColor: '#E5E7EB'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: '1px solid',
                                        borderColor: '#E5E7EB'
                                    },
                                    '.MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.7,
                                        py: 0.5,
                                        px: 1.2
                                    },
                                    mb: 1
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300,
                                            minWidth: 200,
                                            borderRadius: 1.2,
                                            '& .MuiMenuItem-root': {
                                                fontSize: '0.875rem',
                                                minHeight: '36px',
                                                padding: '6px 12px'
                                            }
                                        }
                                    }
                                }}
                                renderValue={() => {
                                    const FlagComponent = selectedCountry.Flag
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                            {FlagComponent ? (
                                                <FlagComponent
                                                    width={FLAG_SIZES.listItem.width}
                                                    height={FLAG_SIZES.listItem.height}
                                                    style={{ borderRadius: 2 }}
                                                />
                                            ) : (
                                                <PublicIcon sx={{ width: 16, height: 12, color: 'text.secondary' }} />
                                            )}
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#111827', fontSize: 13, fontWeight: 500 }}
                                            >
                                                {selectedCountry.label}
                                            </Typography>
                                        </Box>
                                    )
                                }}
                            >
                                {countryOptions.map(country => {
                                    const CountryFlag = country.Flag
                                    return (
                                        <MenuItem key={country.code} value={country.code}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {CountryFlag ? (
                                                    <CountryFlag
                                                        width={FLAG_SIZES.listItem.width}
                                                        height={FLAG_SIZES.listItem.height}
                                                        style={{ borderRadius: 2 }}
                                                    />
                                                ) : (
                                                    <PublicIcon
                                                        sx={{ width: 20, height: 15, color: 'text.secondary' }}
                                                    />
                                                )}
                                                <span>{country.label}</span>
                                                {country.code === selectedCountry.code && (
                                                    <CheckCircleIcon
                                                        sx={{
                                                            color: green[500],
                                                            fontSize: 16,
                                                            marginLeft: 'auto'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                        </>
                    )}
                    {/* Change Currency - Hidden on offers page */}
                    {!isOffersPage && (
                        <>
                            <Typography
                                variant="subtitle2"
                                sx={{ color: '#232323', fontSize: 15, fontWeight: 600, mb: 1.5, mt: 2 }}
                            >
                                {t('changeCurrency')}
                            </Typography>
                            <Select
                                value={selectedCurrency.code}
                                onChange={handleCurrencyChange}
                                open={currencySelectOpen}
                                onOpen={handleCurrencySelectOpen}
                                onClose={handleCurrencySelectClose}
                                variant="outlined"
                                size="small"
                                sx={{
                                    width: '100%',
                                    borderRadius: 1.2,
                                    fontSize: 13,
                                    minHeight: 30,
                                    background: '#fff',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        border: '1px solid',
                                        borderColor: '#E5E7EB'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        border: '1px solid',
                                        borderColor: '#E5E7EB'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: '1px solid',
                                        borderColor: '#E5E7EB'
                                    },
                                    '.MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.7,
                                        py: 0.5,
                                        px: 1.2
                                    },
                                    mb: 1
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300,
                                            minWidth: 200,
                                            borderRadius: 1.2,
                                            '& .MuiMenuItem-root': {
                                                fontSize: '0.875rem',
                                                minHeight: '36px',
                                                padding: '6px 12px'
                                            }
                                        }
                                    }
                                }}
                                renderValue={() => (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                        <selectedCurrency.Flag
                                            width={FLAG_SIZES.listItem.width}
                                            height={FLAG_SIZES.listItem.height}
                                            style={{ borderRadius: 2 }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#111827', fontSize: 13, fontWeight: 500 }}
                                        >
                                            {tCurrency(`${selectedCurrency.code}-text`) || selectedCurrency.label} (
                                            {selectedCurrency.code})
                                        </Typography>
                                    </Box>
                                )}
                            >
                                {currencyOptions.map(curr => {
                                    const currencyName = tCurrency(`${curr.code}-text`) || curr.label
                                    return (
                                        <MenuItem
                                            key={curr.code}
                                            value={curr.code}
                                            sx={{ fontSize: 13, minHeight: 32 }}
                                        >
                                            <ListItemIcon>
                                                <curr.Flag
                                                    width={FLAG_SIZES.listItem.width}
                                                    height={FLAG_SIZES.listItem.height}
                                                    style={{ borderRadius: 2 }}
                                                />
                                            </ListItemIcon>
                                            {currencyName} ({curr.code})
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    )
}

// Export the component as default
export default LanguageCurrencyDropdown
